import React, { useState, useEffect } from 'react';
import { api } from './utils/api';
import { ImageItem } from './types';
import { RowsPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';


// Request presigned URL and return URL + content-type for previewing
async function getPresignedUrlAndType(key: string): Promise<{ url: string; contentType: string | null }> {
    const resp = await api.get(`/download?key=${encodeURIComponent(key)}`);
    if (!resp.ok) throw new Error(`Failed to get download URL: ${resp.status}`);
    const { url, contentType } = await resp.json();
    console.log('got ', url, contentType)
    // If backend didn't provide a contentType, guess based on file extension
    if (!contentType) {
        const ext = key.split('.').pop()?.toLowerCase() || '';
        const map: Record<string, string> = {
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            webp: 'image/webp',
            gif: 'image/gif',
            svg: 'image/svg+xml',
            pdf: 'application/pdf',
            mp4: 'video/mp4',
            webm: 'video/webm',
            mp3: 'audio/mpeg',
            wav: 'audio/wav',
        };
        return { url, contentType: map[ext] ?? null };
    }
    console.log('got ', url, contentType)
    return { url, contentType: contentType ?? null };
}

async function uploadImages(files: FileList) {
    const form = new FormData();
    for (let i = 0; i < files.length; i++) {
        form.append("file", files[i]);
    }

    try {
        const res = await api.post("/uploadImage", form);

        if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
        return await res.json();
    } catch (err) {
        console.error('upload error', err);
        throw err;
    }
}

const Admin: React.FC = () => {
    const [images, setImages] = useState<ImageItem[]>([]);
    const [preview, setPreview] = useState<{ url: string; contentType: string | null } | null>(null);
    const [uploading, setUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [tags, setTags] = useState('');
    const [openTag, setOpenTag] = useState<string | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [lightboxTag, setLightboxTag] = useState<string | null>(null);

    const fetchFiles = async () => {
        try {
            const res = await api.get(`/listObjectsByTags?tags=${encodeURIComponent(tags)}`);
            if (!res.ok) throw new Error(`List failed: ${res.status}`);
            const data: ImageItem[] = await res.json();
            setImages(data || []);
        } catch (err) {
            console.error('failed to fetch file list', err);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const handleUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            await uploadImages(files);
            await fetchFiles();
            setSelectedFiles(null); // Clear selected files after upload
        } catch (err) {
            console.error('upload failed', err);
        } finally {
            setUploading(false);
        }
    };

    const imagesByTag = images.reduce((acc: Record<string, ImageItem[]>, image) => {
        (image.tags || []).forEach(tag => {
            if (!acc[tag]) {
                acc[tag] = [];
            }
            acc[tag].push(image);
            console.log('dawei got image', image)
        });
        return acc;
    }, {});

    const handlePhotoClick = (tag: string, index: number) => {
        setLightboxTag(tag);
        setLightboxIndex(index);
    };

    return (
        <div>
            <input
                name="Upload Image"
                type="file"
                accept="image/*"
                multiple
                onChange={e => setSelectedFiles(e.target.files)}
            />
            <button onClick={() => handleUpload(selectedFiles)} disabled={!selectedFiles || selectedFiles.length === 0 || uploading}>
                Submit
            </button>
            {uploading && <p>Uploading...</p>}

            <div style={{ marginTop: 16 }}>
                <input
                    type="text"
                    placeholder="Enter tags, comma-separated"
                    value={tags}
                    onChange={e => setTags(e.target.value)}
                />
                <button onClick={fetchFiles}>Search</button>

                {Object.keys(imagesByTag).length > 0 ? (
                    Object.entries(imagesByTag)
                        .filter(([tag]) => tags.split(',').map(t => t.trim()).includes(tag))
                        .map(([tag, imgs]) => (
                            <section key={tag} style={{ marginBottom: '1rem', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                                <details open={openTag === tag} onToggle={(e) => setOpenTag((e.target as HTMLDetailsElement).open ? tag : null)}>
                                    <summary style={{ padding: '0.75rem 1rem', background: '#fafafa', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>{tag} ({imgs.length})</span>
                                    </summary>
                                    <div style={{ padding: '1rem' }}>
                                        <RowsPhotoAlbum
                                            photos={imgs.map(img => ({ src: img.thumbnailUrl, width: img.width || 200, height: img.height || 150 }))}
                                            targetRowHeight={200}
                                            onClick={({ index }) => handlePhotoClick(tag, index)}
                                        />
                                    </div>
                                </details>
                            </section>
                        ))
                ) : (
                    <div>No images found for the specified tags.</div>
                )}

                {lightboxTag && lightboxIndex >= 0 && (
                    <Lightbox
                        open={true}
                        close={() => { setLightboxIndex(-1); setLightboxTag(null); }}
                        slides={(imagesByTag[lightboxTag] || []).map(img => ({ src: img.fullImageUrl, alt: img.fileName }))}
                        index={lightboxIndex}
                        plugins={[Zoom]}
                    />
                )}
            </div>
        </div>
    )
}

export default Admin;